import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Icon } from "../lib/Icon";
import { usePortal } from "./state";
import { allTickets, isOpenStatus, type TicketTone } from "./tickets";
import { SectionHeader } from "./components";

const TONE: Record<TicketTone, { bg: string; fg: string }> = {
  amber: { bg: "#fbf2dc", fg: "#9a6a12" },
  blue: { bg: "#e8f0f3", fg: "#2a6f8a" },
  green: { bg: "#e8f4ee", fg: "#1f8a5b" },
  gray: { bg: "#f0efe9", fg: "#6b706a" },
};

export function TicketStatus({ status, tone }: { status: string; tone: TicketTone }) {
  const t = TONE[tone] ?? TONE.gray;
  return <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 2, padding: "3px 8px", background: t.bg, color: t.fg, whiteSpace: "nowrap" }}>{status}</span>;
}

export function PortalTickets() {
  const { brand, brandId } = usePortal();
  const [tab, setTab] = useState<"open" | "solved">("open");
  const mine = allTickets().filter((t) => t.brand === brandId);
  const shown = mine.filter((t) => (tab === "open" ? isOpenStatus(t.status) : !isOpenStatus(t.status)));
  const openCount = mine.filter((t) => isOpenStatus(t.status)).length;

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "30px 28px 64px", display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px", color: "#1d241f" }}>My tickets</h1>
        <span style={{ flex: 1 }} />
        <Link to="/portal/new-ticket" style={{ textDecoration: "none", border: 0, background: brand.ink, color: "#fff", borderRadius: 3, padding: "9px 16px", fontSize: 12, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 7 }}>
          <Icon name="plus" size={15} color="#fff" />
          Submit a ticket
        </Link>
      </div>

      <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #ececec" }}>
        {(["open", "solved"] as const).map((k) => {
          const on = tab === k;
          return (
            <button key={k} onClick={() => setTab(k)} style={{ border: 0, background: "transparent", padding: "9px 2px", marginRight: 22, fontSize: 13, fontWeight: 600, color: on ? brand.ink : "#8a8e88", borderBottom: `2px solid ${on ? brand.ink : "transparent"}`, marginBottom: -1, cursor: "pointer", textTransform: "capitalize" }}>
              {k}{k === "open" && openCount ? ` · ${openCount}` : ""}
            </button>
          );
        })}
      </div>

      {shown.length === 0 ? (
        <div style={{ border: "1px dashed #dcdad2", background: "#fafaf7", borderRadius: 3, padding: "40px 20px", textAlign: "center" }}>
          <div style={{ fontSize: 14.5, fontWeight: 600, color: "#1d241f" }}>{tab === "open" ? "No open tickets" : "No solved tickets yet"}</div>
          <p style={{ fontSize: 13, color: "#8a8e88", margin: "8px 0 0" }}>
            {tab === "open" ? <>When you open a ticket it shows here. <Link to="/portal/new-ticket" style={{ color: brand.ink }}>Submit one →</Link></> : "Solved tickets will appear here."}
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {shown.map((t) => (
            <Link key={t.id} to="/portal/ticket/$id" params={{ id: t.id }} style={{ textDecoration: "none", color: "inherit", background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "14px 16px", display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 4 }}>
                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11.5, color: "#8a8e88" }}>{t.id}</span>
                  <TicketStatus status={t.status} tone={t.statusTone} />
                </div>
                <div style={{ fontSize: 14.5, fontWeight: 600, color: "#1d241f", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject}</div>
                <div style={{ fontSize: 11.5, color: "#8a8e88", marginTop: 3 }}>{t.space} · with {t.owner} · updated {t.updated}</div>
              </div>
              <Icon name="chevron-right" size={16} color="#bcbfba" />
            </Link>
          ))}
        </div>
      )}
      <div><SectionHeader>Your tickets travel with you — you can also ask from any article.</SectionHeader></div>
    </div>
  );
}
